import React from 'react';
import { Clock, FileAudio, Link as LinkIcon, Music, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RichTextField } from '@/components/common/RichTextField';
import type { PlanElementFormState } from '../../hooks/usePlanElementForm';
import type { PlanElement, MusikkFil } from '@/services/api';
import { musikkAPI } from '@/services/api/musikk.api';

/**
 * Dialog for creating/editing plan elements (overskrifter, hendelser, and sang)
 * 
 * Extracted from PlanEditor.tsx to reduce component size and improve reusability.
 */

interface Props {
  open: boolean;
  mode: 'overskrift' | 'hendelse' | 'sang' | 'musikk_placeholder';
  isEditing: boolean;
  formState: PlanElementFormState;
  element?: PlanElement; // For sang: pass the full element to access musikk data
  onSave: (beskrivelse?: string) => void;
  onCancel: () => void;
  onTransponeringChange?: (transponeringId: number | null) => void;
  onSangVarighetChange?: (varighet: number) => void;
}

const PlanElementDialog: React.FC<Props> = ({
  open,
  mode,
  isEditing,
  formState,
  element,
  onSave,
  onCancel,
  onTransponeringChange,
  onSangVarighetChange,
}) => {
  const {
    navn,
    varighet,
    beskrivelse,
    førProduksjonsstart,
    setNavn,
    setVariighet,
    setBeskrivelse,
    setFørProduksjonsstart,
    navnInputRef,
  } = formState;

  const [selectedTransponeringId, setSelectedTransponeringId] = React.useState<number | null>(null);
  const [sangVarighetMinutter, setSangVarighetMinutter] = React.useState<number>(0);
  const beskrivelseRef = React.useRef<{ getValue: () => string }>(null);

  // Initialize selected transponering and varighet from element
  React.useEffect(() => {
    if (element && mode === 'sang') {
      setSelectedTransponeringId(element.musikk_transponering_id || null);
      // Set varighet from element or default from musikk (avrundet opp)
      const defaultMinutter = element.musikk_lengde_minutter || 0;
      const defaultSekunder = element.musikk_lengde_sekunder || 0;
      const roundedMinutter = defaultSekunder > 0 ? defaultMinutter + 1 : defaultMinutter;
      setSangVarighetMinutter(element.varighet_minutter || roundedMinutter);
    }
  }, [element, mode]);

  const handleOpenFile = async (e: React.MouseEvent, fil: MusikkFil) => {
    if (!element || !element.musikk_id) return;
    
    e.preventDefault(); // Prevent any default behavior
    
    try {
      if (fil.fil_type === 'url' && fil.url) {
        // URL: åpne direkte i ny tab med sikkerhet
        const newWindow = window.open(fil.url, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
      } else if (fil.fil_type === 'upload') {
        // Upload: hent URL fra backend og åpne i ny tab
        const response = await musikkAPI.getFileUrl(element.musikk_id, fil.id);
        const newWindow = window.open(response.url, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
      }
    } catch (error) {
      console.error('Kunne ikke åpne fil:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  const handleSave = () => {
    // Read beskrivelse from ref before saving (as per Quill docs)
    // Pass it directly to onSave to avoid async state issues
    let currentBeskrivelse = beskrivelse;
    if (beskrivelseRef.current) {
      currentBeskrivelse = beskrivelseRef.current.getValue();
      setBeskrivelse(currentBeskrivelse);
    }
    // Pass beskrivelse directly to onSave
    onSave(currentBeskrivelse);
  };

  const handleTransponeringChange = (value: string) => {
    const transponeringId = value === 'original' ? null : parseInt(value, 10);
    setSelectedTransponeringId(transponeringId);
    if (onTransponeringChange) {
      onTransponeringChange(transponeringId);
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatSangLengde = (minutter?: number, sekunder?: number) => {
    if (minutter === undefined && sekunder === undefined) return '';
    const m = minutter || 0;
    const s = sekunder || 0;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const originalSangLengde = element && mode === 'sang' 
    ? formatSangLengde(element.musikk_lengde_minutter, element.musikk_lengde_sekunder)
    : '';
  
  // Rund opp til nærmeste minutt (matcher backend-logikk)
  const originalSangMinutter = element && mode === 'sang'
    ? (element.musikk_lengde_sekunder || 0) > 0 
      ? (element.musikk_lengde_minutter || 0) + 1
      : (element.musikk_lengde_minutter || 0)
    : 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing 
              ? `Rediger ${mode === 'overskrift' ? 'overskrift' : mode === 'sang' ? 'sang' : mode === 'musikk_placeholder' ? 'musikk-placeholder' : 'innslag'}` 
              : `Ny ${mode === 'overskrift' ? 'overskrift' : mode === 'sang' ? 'sang' : mode === 'musikk_placeholder' ? 'musikk-placeholder' : 'hendelse'}`}
          </DialogTitle>
          <DialogDescription>
            {mode === 'overskrift' 
              ? 'Overskrifter grupperer innslag i planen' 
              : mode === 'sang'
              ? 'Sang-innslag med toneart og lydfiler'
              : mode === 'musikk_placeholder'
              ? 'Placeholder for musikk - velg faktisk sang senere når malen brukes i en produksjon'
              : 'Innslag er tidsbaserte aktiviteter i produksjonen'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Navn field */}
          <div className="space-y-2">
            <label htmlFor="navn-input" className="text-sm font-medium">
              Navn <span className="text-destructive">*</span>
            </label>
            <input
              id="navn-input"
              ref={navnInputRef}
              type="text"
              value={navn}
              onChange={(e) => setNavn(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-background"
              autoFocus
              required
            />
          </div>
          
          {/* Overskrift-specific: "Før produksjonsstart" checkbox */}
          {mode === 'overskrift' && (
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg border border-border">
              <Checkbox
                id="før-produksjonsstart"
                checked={førProduksjonsstart}
                onCheckedChange={(checked) => setFørProduksjonsstart(checked === true)}
                className="mt-0.5"
              />
              <div className="flex-1 space-y-1">
                <label 
                  htmlFor="før-produksjonsstart" 
                  className="text-sm font-medium cursor-pointer flex items-center gap-1.5"
                >
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  Plassere før produksjonsstart
                </label>
                <p className="text-xs text-muted-foreground">
                  Alle innslag under denne overskriften vil skje før produksjonens offisielle oppstartstid
                </p>
              </div>
            </div>
          )}
          
          {/* Hendelse-specific: Varighet and Beskrivelse */}
          {mode === 'hendelse' && (
            <>
              <div className="space-y-2">
                <label htmlFor="varighet-input" className="text-sm font-medium">
                  Varighet <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <input
                    id="varighet-input"
                    type="number"
                    value={varighet}
                    onChange={(e) => setVariighet(Number.parseInt(e.target.value, 10) || 0)}
                    onFocus={(e) => e.target.select()}
                    onKeyDown={handleKeyDown}
                    min="0"
                    className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-background"
                    required
                  />
                  <span className="absolute right-3 top-2 text-muted-foreground text-sm">minutter</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <label htmlFor="beskrivelse-input" className="text-sm font-medium">
                  Beskrivelse (valgfritt)
                </label>
                <RichTextField
                  ref={beskrivelseRef}
                  id="beskrivelse-input"
                  value={beskrivelse}
                  placeholder="Utdypende informasjon om innslaget"
                  rows={3}
                />
              </div>
            </>
          )}

          {/* Musikk-placeholder-specific: Varighet (estimert) and Beskrivelse */}
          {mode === 'musikk_placeholder' && (
            <>
              <div className="space-y-2">
                <label htmlFor="varighet-input" className="text-sm font-medium">
                  Estimert varighet (valgfritt)
                </label>
                <div className="relative">
                  <input
                    id="varighet-input"
                    type="number"
                    value={varighet || ''}
                    onChange={(e) => setVariighet(Number.parseInt(e.target.value, 10) || 0)}
                    onFocus={(e) => e.target.select()}
                    onKeyDown={handleKeyDown}
                    min="0"
                    placeholder="5"
                    className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-background"
                  />
                  <span className="absolute right-3 top-2 text-muted-foreground text-sm">minutter</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Dette er bare et estimat. Faktisk varighet settes når du velger sang i produksjonen.
                </p>
              </div>
              
              <div className="space-y-2">
                <label htmlFor="beskrivelse-input" className="text-sm font-medium">
                  Beskrivelse (valgfritt)
                </label>
                <RichTextField
                  ref={beskrivelseRef}
                  id="beskrivelse-input"
                  value={beskrivelse}
                  placeholder="f.eks. Rolig lovprisningssang for å sette stemning"
                  rows={3}
                />
              </div>
            </>
          )}

          {/* Sang-specific: Varighet, Toneart, Beskrivelse, and Filer */}
          {mode === 'sang' && element && (
            <>
              <Separator className="my-4" />

              {/* Varighet med info om original sanglengde */}
              <div className="space-y-2">
                <label htmlFor="sang-varighet-input" className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  Varighet
                </label>
                <div className="relative">
                  <input
                    id="sang-varighet-input"
                    type="number"
                    value={sangVarighetMinutter}
                    onChange={(e) => {
                      const newValue = Number.parseInt(e.target.value, 10) || 0;
                      setSangVarighetMinutter(newValue);
                      if (onSangVarighetChange) {
                        onSangVarighetChange(newValue);
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                    min="0"
                    className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-slate-50/50 focus:bg-white"
                  />
                  <span className="absolute right-3 top-2 text-muted-foreground text-sm">minutter</span>
                </div>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Music className="h-3 w-3" />
                  Sanglengde: {originalSangLengde || '—'} • Kan overstyres til kortere eller lengre varighet
                </p>
              </div>
              
              {/* Toneart/Transponering */}
              <div className="space-y-2">
                <label htmlFor="toneart-select" className="text-sm font-medium flex items-center gap-2">
                  <Music className="h-4 w-4 text-primary" />
                  Toneart
                </label>
                <Select 
                  value={selectedTransponeringId === null ? 'original' : selectedTransponeringId.toString()} 
                  onValueChange={handleTransponeringChange}
                >
                  <SelectTrigger id="toneart-select" className="bg-slate-50/50 focus:bg-white">
                    <SelectValue placeholder="Velg toneart" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="original">
                      Original ({element.musikk_grunntone || 'C'} {element.musikk_toneart_type || 'dur'})
                    </SelectItem>
                    {element.musikk_transponeringer && element.musikk_transponeringer.length > 0 && (
                      <>
                        <Separator className="my-1" />
                        {element.musikk_transponeringer.map((trans) => (
                          <SelectItem key={trans.id} value={trans.id.toString()}>
                            {trans.navn} ({trans.grunntone} {trans.toneart_type})
                            <span className="text-muted-foreground text-xs ml-2">
                              {trans.semitoner > 0 ? '+' : ''}{trans.semitoner}
                            </span>
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Beskrivelse */}
              <div className="space-y-2">
                <label htmlFor="sang-beskrivelse-input" className="text-sm font-medium">
                  Beskrivelse (valgfritt)
                </label>
                <textarea
                  id="sang-beskrivelse-input"
                  value={beskrivelse}
                  onChange={(e) => setBeskrivelse(e.target.value)}
                  placeholder="Utdypende informasjon om sangen"
                  rows={3}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-slate-50/50 focus:bg-white resize-none"
                />
                <p className="text-xs text-muted-foreground">Shift+Enter for ny linje</p>
              </div>

              {/* Lydfiler */}
              {element.musikk_filer && element.musikk_filer.length > 0 ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <FileAudio className="h-4 w-4 text-primary" />
                    Lydfiler ({element.musikk_filer.length})
                  </label>
                  <div className="border border-border rounded-lg divide-y divide-border bg-muted/20">
                    {element.musikk_filer.map((fil) => (
                      <button
                        key={fil.id}
                        type="button"
                        onClick={(e) => handleOpenFile(e, fil)}
                        className="w-full p-3 flex items-center gap-3 hover:bg-muted/50 active:bg-muted/70 transition-colors cursor-pointer text-left group"
                        title={fil.fil_type === 'url' ? `Åpne ${fil.url}` : 'Åpne fil'}
                      >
                        <div className="flex-shrink-0">
                          {fil.fil_type === 'upload' ? (
                            <FileAudio className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                          ) : (
                            <LinkIcon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">{fil.navn}</p>
                          {fil.fil_type === 'upload' && fil.filstorrelse && (
                            <p className="text-xs text-muted-foreground">
                              {formatFileSize(fil.filstorrelse)}
                            </p>
                          )}
                        </div>
                        <Badge variant="outline" className="shrink-0 text-xs">
                          {fil.fil_type === 'upload' ? 'Fil' : 'URL'}
                        </Badge>
                        <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors opacity-0 group-hover:opacity-100" />
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <ExternalLink className="h-3 w-3" />
                    Klikk på en fil for å åpne i nytt vindu
                  </p>
                </div>
              ) : (
                <div className="p-3 bg-muted/20 border border-border rounded-lg">
                  <p className="text-sm text-muted-foreground text-center flex items-center justify-center gap-2">
                    <FileAudio className="h-4 w-4" />
                    Ingen lydfiler lagt til for denne sangen
                  </p>
                </div>
              )}
            </>
          )}
        </div>
        
        <DialogFooter>
          <Button 
            variant="outline"
            onClick={onCancel}
          >
            Avbryt
          </Button>
          <Button 
            onClick={handleSave}
          >
            {isEditing ? 'Oppdater' : 'Legg til'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PlanElementDialog;
